<script setup lang="ts">
import { computed, ref } from 'vue';
import { Check, X, Link, FileJson, Wand2, Trash2 } from 'lucide-vue-next';

// 胶水集成：使用 shadcn-vue 组件构建输入 UI
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * 奇穴编码输入组件
 * @description 使用 Textarea 组件优化奇穴编码输入体验
 */

interface Props {
  modelValue: string;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isFocused = ref(false);

const talentCode = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value)
});

const codeLength = computed(() => talentCode.value.length);

const validateCode = (): boolean => {
  if (!talentCode.value.trim()) {
    return false;
  }
  try {
    const parsed = JSON.parse(talentCode.value);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
};

const isValid = computed(() => {
  if (!talentCode.value) return null;
  return validateCode();
});

const formatCode = () => {
  try {
    const parsed = JSON.parse(talentCode.value);
    talentCode.value = JSON.stringify(parsed, null, 2);
  } catch {
    // 格式化失败则保持原样
  }
};

defineExpose({
  validate: validateCode,
  format: formatCode
});
</script>

<template>
  <div class="space-y-4">
    <!-- 状态栏 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Label class="text-sm font-medium">配装编码</Label>
        <Badge 
          v-if="isValid !== null"
          :variant="isValid ? 'default' : 'destructive'"
          class="gap-1 text-xs"
        >
          <Check v-if="isValid" class="w-3 h-3" />
          <X v-else class="w-3 h-3" />
          {{ isValid ? '格式正确' : '格式错误' }}
        </Badge>
        <Badge v-else variant="secondary" class="text-xs">待输入</Badge>
      </div>
      <span class="text-xs text-muted-foreground">
        {{ codeLength }} 字符
      </span>
    </div>

    <!-- 输入框 -->
    <div 
      class="relative group"
      :class="[
        'rounded-lg border-2 transition-all duration-200',
        isFocused ? 'border-primary ring-2 ring-primary/20' : 'border-border/60',
        isValid === false ? 'border-destructive ring-2 ring-destructive/20' : ''
      ]"
    >
      <Textarea
        v-model="talentCode"
        class="min-h-[140px] font-mono text-sm resize-y border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
        placeholder='在魔盒奇穴模拟器中复制【配装编码】

例如：[{"id":"28593","icon":16204,"name":"日月晦"},{"id":"5974","icon":3819,"name":"血泪成悦"}]'
        @focus="isFocused = true"
        @blur="isFocused = false"
      />
    </div>

    <!-- 操作按钮 -->
    <div class="flex flex-wrap items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        class="gap-1.5"
        :disabled="!talentCode || !isValid"
        @click="formatCode"
      >
        <Wand2 class="w-3.5 h-3.5" />
        格式化
      </Button>
      <Button
        variant="outline"
        size="sm"
        class="gap-1.5"
        :disabled="!talentCode"
        @click="talentCode = ''"
      >
        <Trash2 class="w-3.5 h-3.5" />
        清空
      </Button>
    </div>

    <!-- 提示信息 -->
    <Alert class="bg-secondary/30 border-primary/20">
      <div class="flex flex-col gap-2">
        <AlertDescription class="flex items-start gap-2 text-xs">
          <Link class="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <span>
            请前往
            <a 
              href="https://www.jx3box.com/macro/talent/" 
              target="_blank" 
              class="text-primary hover:underline font-medium"
            >
              https://www.jx3box.com/macro/talent/
            </a>
            复制【配装编码】编码
          </span>
        </AlertDescription>
        <AlertDescription class="flex items-start gap-2 text-xs">
          <FileJson class="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <span>编码格式为 JSON 数组，包含奇穴的 id、icon、name 字段</span>
        </AlertDescription>
      </div>
    </Alert>
  </div>
</template>
